/**
 * @fileoverview Settings Section - Component Library
 *
 * Purpose: Display all settings and branding components
 * Used by: Component library main page
 */

import { ComponentShowcase } from "./-ui.component-showcase";
import { ComponentHeader } from "./-ui.component-header";
import { VariantGrid, VariantItem } from "./-ui.variant-grid";
import { ThemeSwitcher, LanguageSwitcher } from "@/ui/settings";
import { Logo } from "@/ui/layout/logo";
import { Button } from "@/ui/primitives/button";
import { Separator } from "@/ui/primitives/separator";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "@/ui/layout/breadcrumb";
import {
  Home,
  ChevronRight,
  LayoutDashboard,
  Inbox,
  HelpCircle,
} from "lucide-react";
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarCheckboxItem,
} from "@/ui/composed/menus/menubar";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from "@/ui/layout/navigation-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/ui/layout/pagination";
import { useState } from "react";

// Menubar Demo
function MenubarDemo() {
  const [showBookmarks, setShowBookmarks] = useState(true);
  const [showUrls, setShowUrls] = useState(false);

  return (
    <Menubar>
      <MenubarMenu>
        <MenubarTrigger>File</MenubarTrigger>
        <MenubarContent>
          <MenubarItem>
            New Tab <MenubarShortcut>⌘T</MenubarShortcut>
          </MenubarItem>
          <MenubarItem>
            New Window <MenubarShortcut>⌘N</MenubarShortcut>
          </MenubarItem>
          <MenubarSeparator />
          <MenubarItem>Share</MenubarItem>
          <MenubarSeparator />
          <MenubarItem>
            Print <MenubarShortcut>⌘P</MenubarShortcut>
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>

      <MenubarMenu>
        <MenubarTrigger>Edit</MenubarTrigger>
        <MenubarContent>
          <MenubarItem>
            Undo <MenubarShortcut>⌘Z</MenubarShortcut>
          </MenubarItem>
          <MenubarItem>
            Redo <MenubarShortcut>⇧⌘Z</MenubarShortcut>
          </MenubarItem>
          <MenubarSeparator />
          <MenubarItem>Cut</MenubarItem>
          <MenubarItem>Copy</MenubarItem>
          <MenubarItem>Paste</MenubarItem>
        </MenubarContent>
      </MenubarMenu>

      <MenubarMenu>
        <MenubarTrigger>View</MenubarTrigger>
        <MenubarContent>
          <MenubarCheckboxItem
            checked={showBookmarks}
            onCheckedChange={setShowBookmarks}
          >
            Show Bookmarks Bar
          </MenubarCheckboxItem>
          <MenubarCheckboxItem checked={showUrls} onCheckedChange={setShowUrls}>
            Show Full URLs
          </MenubarCheckboxItem>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}

// Pagination Demo
function PaginationDemo() {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 10;

  return (
    <div className="space-y-4">
      <p className="text-center text-sm text-muted-foreground">
        Current page: {currentPage} of {totalPages}
      </p>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage((p) => Math.max(1, p - 1));
              }}
            />
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href="#"
              isActive={currentPage === 1}
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage(1);
              }}
            >
              1
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href="#"
              isActive={currentPage === 2}
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage(2);
              }}
            >
              2
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href="#"
              isActive={currentPage === 3}
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage(3);
              }}
            >
              3
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href="#"
              isActive={currentPage === totalPages}
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage(totalPages);
              }}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage((p) => Math.min(totalPages, p + 1));
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}

export function SettingsSection() {
  return (
    <>
      {/* Breadcrumb */}
      <div id="breadcrumb" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Breadcrumb"
          description="Hierarchical navigation showing the user's location in the site."
          filePath="src/ui/layout/breadcrumb.tsx"
        />

        <ComponentShowcase
          title="Breadcrumb Examples"
          description="Navigation breadcrumbs for showing page hierarchy"
        >
          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Simple Breadcrumb</p>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">Home</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Settings</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">With Icon</p>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">
                      <Home className="h-4 w-4" />
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/products">Products</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Electronics</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                With Ellipsis (Collapsed)
              </p>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">Home</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbEllipsis />
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/products">Products</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Current Page</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Custom Separator</p>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">Home</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <ChevronRight className="h-4 w-4" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/docs">Documentation</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <ChevronRight className="h-4 w-4" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>Components</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Menubar */}
      <div id="menubar" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Menubar"
          description="Application-style menu bar (File, Edit, View menus)."
          filePath="src/ui/composed/menus/menubar.tsx"
        />

        <ComponentShowcase
          title="Menubar Example"
          description="Desktop-style menu bar with dropdowns"
        >
          <MenubarDemo />
        </ComponentShowcase>
      </div>

      {/* Navigation Menu */}
      <div id="navigation-menu" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Navigation Menu"
          description="Site navigation with dropdown content panels."
          filePath="src/ui/layout/navigation-menu.tsx"
        />

        <ComponentShowcase
          title="Navigation Menu Example"
          description="Hover or click to see dropdown navigation"
        >
          <div className="flex justify-center">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Getting Started</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[400px] gap-3 p-6">
                      <NavigationMenuLink
                        href="#"
                        className="block space-y-1 rounded-md p-3 hover:bg-accent"
                      >
                        <div className="text-sm font-medium">Introduction</div>
                        <p className="text-sm text-muted-foreground">
                          Learn how to get started with our platform
                        </p>
                      </NavigationMenuLink>
                      <NavigationMenuLink
                        href="#"
                        className="block space-y-1 rounded-md p-3 hover:bg-accent"
                      >
                        <div className="text-sm font-medium">Installation</div>
                        <p className="text-sm text-muted-foreground">
                          Step-by-step installation guide
                        </p>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Components</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[400px] gap-3 p-6">
                      <NavigationMenuLink
                        href="#"
                        className="block space-y-1 rounded-md p-3 hover:bg-accent"
                      >
                        <div className="text-sm font-medium">Buttons</div>
                        <p className="text-sm text-muted-foreground">
                          Clickable button components
                        </p>
                      </NavigationMenuLink>
                      <NavigationMenuLink
                        href="#"
                        className="block space-y-1 rounded-md p-3 hover:bg-accent"
                      >
                        <div className="text-sm font-medium">Forms</div>
                        <p className="text-sm text-muted-foreground">
                          Form input components
                        </p>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </ComponentShowcase>
      </div>

      {/* Pagination */}
      <div id="pagination" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Pagination"
          description="Page navigation controls for multi-page content."
          filePath="src/ui/layout/pagination.tsx"
        />

        <ComponentShowcase
          title="Pagination Example"
          description="Click to navigate between pages"
        >
          <PaginationDemo />
        </ComponentShowcase>
      </div>

      {/* Logo */}
      <div id="logo" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Logo"
          description="Application logo SVG component with customizable size."
          filePath="src/ui/layout/logo.tsx"
        />

        <ComponentShowcase
          title="Logo Sizes & Colors"
          description="Logo component in different sizes and colors"
        >
          <VariantGrid columns={4}>
            <VariantItem label="Small (24px)">
              <Logo width={24} height={24} />
            </VariantItem>
            <VariantItem label="Default (40px)">
              <Logo />
            </VariantItem>
            <VariantItem label="Large (60px)">
              <Logo width={60} height={60} />
            </VariantItem>
            <VariantItem label="Extra Large (80px)">
              <Logo width={80} height={80} />
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Logo with Custom Colors"
          description="Logo inherits color from parent text color"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Primary">
              <div className="text-primary">
                <Logo width={48} height={48} />
              </div>
            </VariantItem>
            <VariantItem label="Muted">
              <div className="text-muted-foreground">
                <Logo width={48} height={48} />
              </div>
            </VariantItem>
            <VariantItem label="Destructive">
              <div className="text-destructive">
                <Logo width={48} height={48} />
              </div>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Sidebar */}
      <div id="sidebar" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Sidebar"
          description="Comprehensive collapsible sidebar with navigation menus."
          filePath="src/ui/layout/sidebar.tsx"
        />

        <ComponentShowcase
          title="Sidebar Structure"
          description="Visual representation of sidebar components"
        >
          <div className="grid gap-4">
            <div className="space-y-4 rounded-lg border p-6">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Component Structure</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3 rounded-md bg-muted p-3">
                    <LayoutDashboard className="mt-0.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="font-medium">SidebarHeader</div>
                      <div className="text-xs text-muted-foreground">
                        Sticky header with logo/branding
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-md bg-muted p-3">
                    <Inbox className="mt-0.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="font-medium">SidebarContent</div>
                      <div className="text-xs text-muted-foreground">
                        Scrollable navigation menus and groups
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-md bg-muted p-3">
                    <HelpCircle className="mt-0.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="font-medium">SidebarFooter</div>
                      <div className="text-xs text-muted-foreground">
                        Sticky footer with user profile/help
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Menu Components</h4>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarMenu
                    </code>{" "}
                    →{" "}
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarMenuItem
                    </code>{" "}
                    →{" "}
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarMenuButton
                    </code>
                  </div>
                  <div>
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarGroup
                    </code>{" "}
                    →{" "}
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarGroupLabel
                    </code>{" "}
                    +{" "}
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarGroupContent
                    </code>
                  </div>
                  <div>
                    <code className="rounded bg-background px-1.5 py-0.5">
                      SidebarTrigger
                    </code>{" "}
                    - Toggle button for main content
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="mb-3 text-sm text-muted-foreground">
                <strong className="text-foreground">Note:</strong> Due to its
                complexity (772 lines, 16+ sub-components, context provider),
                the Sidebar component requires a dedicated implementation. It
                cannot be demonstrated inline without affecting this page's
                navigation.
              </p>
              <p className="text-sm text-muted-foreground">
                The component is fully installed and ready to use. Visit the
                links below to see 30+ working examples.
              </p>
            </div>
          </div>
        </ComponentShowcase>

        <ComponentShowcase
          title="More Variants Available"
          description="shadcn provides 30+ pre-built sidebar variants"
        >
          <div className="space-y-4 rounded-lg border bg-muted/50 p-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">
                Pre-built Sidebar Blocks
              </h4>
              <p className="text-sm text-muted-foreground">
                Install complete sidebar implementations with different
                features:
              </p>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="space-y-1 rounded border bg-background p-3">
                  <span className="font-medium">sidebar-01 to sidebar-05</span>
                  <p className="text-xs text-muted-foreground">
                    Basic layouts with different menu structures
                  </p>
                </div>
                <div className="space-y-1 rounded border bg-background p-3">
                  <span className="font-medium">sidebar-07 to sidebar-09</span>
                  <p className="text-xs text-muted-foreground">
                    Icon-only collapsed states
                  </p>
                </div>
                <div className="space-y-1 rounded border bg-background p-3">
                  <span className="font-medium">sidebar-10 to sidebar-12</span>
                  <p className="text-xs text-muted-foreground">
                    With dropdown submenus
                  </p>
                </div>
                <div className="space-y-1 rounded border bg-background p-3">
                  <span className="font-medium">sidebar-14 to sidebar-16</span>
                  <p className="text-xs text-muted-foreground">
                    With search and user profiles
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Key Features</h4>
              <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
                <li>Collapsible/expandable states</li>
                <li>Mobile responsive (sheet on mobile, sidebar on desktop)</li>
                <li>Keyboard shortcuts (⌘B to toggle)</li>
                <li>Persistent state across sessions</li>
                <li>Customizable width and appearance</li>
                <li>16+ composable sub-components</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button size="sm" variant="outline" asChild>
                <a
                  href="https://ui.shadcn.com/docs/components/sidebar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Full Documentation
                </a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a
                  href="https://ui.shadcn.com/blocks#sidebar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Browse All Variants
                </a>
              </Button>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Theme Switcher */}
      <div id="theme-switcher" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Theme Switcher"
          description="Theme selection dropdown with light/dark/system options."
          filePath="src/ui/settings/theme-switcher.tsx"
        />

        <ComponentShowcase
          title="Theme Switcher Examples"
          description="Dropdown for switching between light, dark, and system themes"
        >
          <VariantGrid columns={2}>
            <VariantItem label="Default Size">
              <ThemeSwitcher />
            </VariantItem>
            <VariantItem label="Full Width">
              <ThemeSwitcher triggerClass="w-full" />
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Usage Example"
          description="Theme switcher is already active in the header of this page"
        >
          <div className="rounded-lg border bg-muted/50 p-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                The theme switcher in the top-right corner of this page is a
                live example. Try clicking it to switch between light, dark, and
                system themes!
              </p>
              <div className="flex items-center gap-3 rounded-md border bg-background p-4">
                <span className="text-sm font-medium">
                  Current theme control:
                </span>
                <ThemeSwitcher />
              </div>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Language Switcher */}
      <div id="language-switcher" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Language Switcher"
          description="Language selection dropdown for multi-language support."
          filePath="src/ui/settings/language-switcher.tsx"
        />

        <ComponentShowcase
          title="Language Switcher Example"
          description="Dropdown for switching between available languages (English/Spanish)"
        >
          <VariantGrid columns={2}>
            <VariantItem label="Default">
              <LanguageSwitcher />
            </VariantItem>
            <VariantItem label="In Context">
              <div className="flex items-center gap-2 rounded-md border bg-background p-4">
                <span className="text-sm">Choose language:</span>
                <LanguageSwitcher />
              </div>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>

        <ComponentShowcase
          title="Available Languages"
          description="Current language options configured in the app"
        >
          <div className="rounded-lg border bg-muted/50 p-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span className="text-sm font-medium">English</span>
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  en
                </code>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span className="text-sm font-medium">Spanish</span>
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  es
                </code>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Add more languages in{" "}
                <code className="rounded bg-muted px-1 py-0.5">
                  src/ui/settings/language-switcher.tsx
                </code>
              </p>
            </div>
          </div>
        </ComponentShowcase>
      </div>
    </>
  );
}
